import { BookIcon } from '@/assets/icons/Book'
import { useWebTour } from '@/designs/new/contexts/WebTourContext'
import { useAppDispatch, useAppSelector } from '@/hooks/storeHooks/storehooks'
import { setWebTour } from '@/redux-store/slices/menuItemSlice/menuItemSlice'
import { TooltipRenderProps } from 'react-joyride'
import { useNavigate } from 'react-router-dom'
import { isStepAllowed } from './webTourUtils'
import { WEB_TOUR_STEPS } from '@/designs/new/constants/webTourSteps'

const CustomModal = ({
  index,
  // size,
  step,
  backProps,
  primaryProps,
  closeProps,
  tooltipProps,
}: TooltipRenderProps) => {
  const { stopTour, setTourStepIndex } = useWebTour()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isInteractive = step?.title === 'Select a Node'
  const allowedPaths = useAppSelector(state => state.menu.paths)
  const allowedStepsCount = WEB_TOUR_STEPS.filter(s =>
    isStepAllowed(s, allowedPaths),
  ).length
  const currentDisplayIndex = WEB_TOUR_STEPS.slice(0, index).filter(s =>
    isStepAllowed(s, allowedPaths),
  ).length

  const arrowClass = {
    right: '-left-2 top-1/2 -translate-y-1/2',
    left: '-right-2 top-1/2 -translate-y-1/2',
    top: '-bottom-2 left-1/2 -translate-x-1/2',
    bottom: '-top-2 left-1/2 -translate-x-1/2',
    // hidden: 'hidden',
  }[step.placement?.split('-')[0]]

  console.log(step, 'step:', step.placement)

  if (index === 0) {
    return (
      <div
        {...tooltipProps}
        className='w-[360px] rounded-lg bg-primary shadow-xl'
      >
        <div className='flex items-center justify-between px-5 pt-5 pb-3'>
          <div className='flex items-center gap-2'>
            <BookIcon />
            <h2 className='text-[1.125rem] font-semibold text-primary-foreground'>
              Welcome to NIC Portal
            </h2>
          </div>

          <button
            {...closeProps}
            onClick={() => {
              dispatch(setWebTour(false))
              stopTour()
              navigate('/home')
            }}
          >
            ✕
          </button>
        </div>

        <div className='px-5 pb-3 text-primary-foreground text-[0.875rem]'>
          {step.content}
        </div>

        <div className='flex justify-end gap-2 border-t p-4'>
          <button
            {...backProps}
            onClick={e => {
              if (index === 0) {
                dispatch(setWebTour(false))
              }

              backProps?.onClick?.(e)
            }}
            className='px-[0.75rem] p-[0.25rem] flex justify-center items-center border border-border rounded-radius-md text-accent hover:text-accent-foreground hover:bg-accent-hover'
          >
            Back
          </button>

          <button
            {...primaryProps}
            onClick={e => {
              if (currentDisplayIndex === allowedStepsCount - 1) {
                dispatch(setWebTour(false))
              }

              primaryProps?.onClick?.(e)
            }}
            className='px-[0.75rem] p-[0.25rem] flex justify-center items-center border border-border rounded-radius-md bg-accent text-accent-foreground hover:bg-accent-hover'
          >
            {currentDisplayIndex === allowedStepsCount - 1
              ? 'Finish'
              : `Next (${currentDisplayIndex + 1}/${allowedStepsCount})`}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      {...tooltipProps}
      className='relative w-[360px] rounded-lg bg-primary shadow-xl'
    >
      {/* Arrow */}
      {step.placement && (
        <div
          className={`absolute ${arrowClass} h-4 w-4 rotate-45 bg-primary`}
        />
      )}

      {isInteractive && step.placement && (
        <div
          className={`absolute ${arrowClass} flex h-5 w-5 items-center justify-center`}
        >
          <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75' />
          <span className='relative inline-flex h-3 w-3 rounded-full bg-accent' />
        </div>
      )}

      <div className='flex justify-between px-5 pt-5 pb-3'>
        <h2 className='font-semibold text-primary-foreground text-[1.125rem]'>
          {step.title}
        </h2>

        <button
          {...closeProps}
          onClick={() => {
            dispatch(setWebTour(false))
            stopTour()
            navigate('/home')
          }}
        >
          ✕
        </button>
      </div>

      <div className='px-5 text-primary-foreground text-[0.875rem]'>
        {step.content}
        {isInteractive && (
          <div className='mt-2 flex items-center gap-1.5 text-sm font-semibold text-danger'>
            <span className='relative flex h-3 w-3'>
              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-danger opacity-95' />
              <span className='relative inline-flex h-3 w-3 rounded-full bg-danger' />
            </span>
            Click a row to continue
          </div>
        )}
      </div>

      <div className='flex items-center justify-between p-5'>
        <button
          onClick={() => {
            dispatch(setWebTour(false))
            stopTour()
            navigate('/home')
          }}
          className='text-accent text-sm'
        >
          Don't show again
        </button>

        <div className='flex gap-2'>
          <button
            {...backProps}
            onClick={e => {
              if (step.target === '[data-testid="table-container"]') {
                setTourStepIndex(index - 1)
                return
              }
              if (index === 0) {
                dispatch(setWebTour(false))
              }

              backProps?.onClick?.(e)
            }}
            className='px-[0.75rem] p-[0.25rem] flex justify-center items-center border border-border rounded-radius-md  text-accent hover:text-accent-foreground hover:bg-accent-hover '
          >
            Back
          </button>

          <button
            {...primaryProps}
            onClick={e => {
              if (step.target === '[data-testid="table-container"]') {
                setTourStepIndex(index + 2)
                return
              }

              if (currentDisplayIndex === allowedStepsCount - 1) {
                dispatch(setWebTour(false))
                navigate('/home')
              }

              primaryProps?.onClick?.(e)
            }}
            className='px-[0.75rem] p-[0.25rem] flex justify-center items-center border border-border rounded-radius-md bg-accent text-accent-foreground'
          >
            {currentDisplayIndex === allowedStepsCount - 1 ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CustomModal
